import { useState, useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Calendar } from 'lucide-react';
import { useGetFeedFixturesQuery, useGetFeedTeamsQuery } from '@/api/feed';
import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { isIndividualSport } from '@low6-pickem/sports-config';

interface FixtureStepProps {
  leagueId?: string;
  sportId?: string;
  selectedFixtureId?: string;
  onSelect?: (fixtureId: string, fixtureDisplay: string, teamIds: string[]) => void;
  /**
   * Batch creation mode (DKPLAT-501 Phase 1): renders checkboxes instead of
   * single-select cards. Use `selectedFixtureIds` + `onToggleFixture` instead of
   * `selectedFixtureId` + `onSelect`. Not supported for individual sports (PGA).
   */
  multiSelect?: boolean;
  selectedFixtureIds?: string[];
  onToggleFixture?: (fixtureId: string, fixtureDisplay: string, teamIds: string[]) => void;
}

const STATUS_COLORS: Record<string, string> = {
  scheduled: 'bg-blue-500',
  live: 'bg-green-500',
  finished: 'bg-gray-500',
  postponed: 'bg-yellow-500',
  cancelled: 'bg-red-500',
};

// Get today's date in ET timezone formatted as YYYY-MM-DD
const getTodayDateET = () => {
  return formatInTimeZone(new Date(), 'America/New_York', 'yyyy-MM-dd');
};

// Get tomorrow's date in ET timezone formatted as YYYY-MM-DD
const getTomorrowDateET = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return formatInTimeZone(tomorrow, 'America/New_York', 'yyyy-MM-dd');
};

export function FixtureStep({
  leagueId,
  sportId,
  selectedFixtureId,
  onSelect,
  multiSelect = false,
  selectedFixtureIds = [],
  onToggleFixture,
}: FixtureStepProps) {
  const isIndividual = sportId ? isIndividualSport(sportId) : false;

  const [tournamentSearch, setTournamentSearch] = useState('');

  // Default to showing fixtures from today to tomorrow
  const [fromDate, setFromDate] = useState(getTodayDateET());
  const [toDate, setToDate] = useState(getTomorrowDateET());

  const { data: teams } = useGetFeedTeamsQuery(leagueId);

  // For individual sports (PGA), don't send dates — fetch all rounds
  const {
    data: fixtures,
    isLoading,
    error,
  } = useGetFeedFixturesQuery(
    leagueId,
    undefined,
    isIndividual ? undefined : fromDate || undefined,
    isIndividual ? undefined : toDate || undefined
  );

  // Create a map of team IDs to team names
  const teamMap = useMemo(() => {
    const map = new Map<string, string>();
    teams?.forEach(team => {
      map.set(team.externalId, team.name);
    });
    return map;
  }, [teams]);

  // For PGA: group rounds by tournament
  const tournamentGroups = useMemo(() => {
    if (!isIndividual || !fixtures) return null;
    const groups = new Map<string, { name: string; rounds: typeof fixtures }>();
    for (const fixture of fixtures) {
      if (!fixture.externalId.includes(':round_')) continue;
      const tournamentId = fixture.externalId.split(':round_')[0];
      const tournamentName = teamMap.get(tournamentId) || fixture.homeTeamId;
      if (!groups.has(tournamentId)) {
        groups.set(tournamentId, { name: tournamentName, rounds: [] });
      }
      groups.get(tournamentId)!.rounds.push(fixture);
    }
    return groups;
  }, [fixtures, isIndividual, teamMap]);

  if (!leagueId) {
    return (
      <Alert>
        <AlertDescription>Please select a sport and league first.</AlertDescription>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading fixtures...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load fixtures. Please try again.</AlertDescription>
      </Alert>
    );
  }

  // Handler to update both fromDate and toDate with the same value
  const handleDateChange = (date: string) => {
    setFromDate(date);
    setToDate(date);
  };

  // Individual sport (PGA): show tournaments with rounds
  if (isIndividual && tournamentGroups) {
    const filteredGroups = tournamentSearch
      ? Array.from(tournamentGroups.entries()).filter(([, { name }]) =>
          name.toLowerCase().includes(tournamentSearch.toLowerCase())
        )
      : Array.from(tournamentGroups.entries());

    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">Select a tournament round for this question</p>

        <Input
          placeholder="Search tournaments..."
          value={tournamentSearch}
          onChange={e => setTournamentSearch(e.target.value)}
        />

        {filteredGroups.length === 0 ? (
          <Alert>
            <AlertDescription>
              {tournamentSearch
                ? `No tournaments matching "${tournamentSearch}".`
                : 'No tournaments found.'}
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4 max-h-[400px] overflow-y-auto">
            {filteredGroups.map(([tournamentId, { name, rounds }]) => (
              <div key={tournamentId}>
                <Label className="text-base font-semibold">{name}</Label>
                <div className="space-y-2 mt-2">
                  {rounds.map(fixture => {
                    const isSelected = selectedFixtureId === fixture.externalId;
                    const roundNum = fixture.externalId.split(':round_')[1];
                    const roundDate = format(
                      parseISO(fixture.scheduledDate),
                      'EEEE, MMMM dd, yyyy'
                    );
                    const fixtureDisplay = `${name} — Round ${roundNum} (${format(parseISO(fixture.scheduledDate), 'EEE, MMM dd')})`;

                    return (
                      <Card
                        key={fixture.externalId}
                        data-testid={`fixture-${fixture.externalId}`}
                        className={`mx-2 p-4 cursor-pointer transition-all hover:shadow-md ${
                          isSelected ? 'ring-2 ring-primary bg-primary/5' : ''
                        }`}
                        onClick={() =>
                          onSelect?.(fixture.externalId, fixtureDisplay, [tournamentId])
                        }
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-medium mb-1">Round {roundNum}</div>
                            <div className="text-sm text-muted-foreground">{roundDate}</div>
                          </div>
                          <Badge className={STATUS_COLORS[fixture.status] || 'bg-gray-500'}>
                            {fixture.status}
                          </Badge>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Team sport: existing date-based fixture selection
  // Sort fixtures ascending by scheduled start time
  const sortedFixtures = fixtures
    ? [...fixtures].sort(
        (a, b) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime()
      )
    : [];

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="fixture-date">
          <Calendar className="inline h-4 w-4 mr-1" />
          Date
        </Label>
        <Input
          id="fixture-date"
          type="date"
          value={fromDate}
          onChange={e => handleDateChange(e.target.value)}
        />
      </div>

      {fixtures && fixtures.length === 0 ? (
        <Alert>
          <AlertDescription>
            No fixtures found for the selected date. Try adjusting the date.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          <Label>
            {multiSelect
              ? `Select Fixtures (${selectedFixtureIds.length} selected)`
              : 'Select a Fixture'}
          </Label>
          {sortedFixtures.map(fixture => {
            const isSelected = multiSelect
              ? selectedFixtureIds.includes(fixture.externalId)
              : selectedFixtureId === fixture.externalId;
            const homeTeam = teamMap.get(fixture.homeTeamId) || 'Home Team';
            const awayTeam = teamMap.get(fixture.awayTeamId) || 'Away Team';
            const fixtureDisplay = `${homeTeam} vs ${awayTeam} - ${format(parseISO(fixture.scheduledDate), 'MMM dd, yyyy h:mm a')}`;
            const teamIds = [fixture.homeTeamId, fixture.awayTeamId];

            const handleClick = () => {
              if (multiSelect) {
                onToggleFixture?.(fixture.externalId, fixtureDisplay, teamIds);
              } else {
                onSelect?.(fixture.externalId, fixtureDisplay, teamIds);
              }
            };

            return (
              <Card
                key={fixture.externalId}
                data-testid={`fixture-${fixture.externalId}`}
                className={`mx-2 p-4 cursor-pointer transition-all hover:shadow-md ${
                  isSelected ? 'ring-2 ring-primary bg-primary/5' : ''
                }`}
                onClick={handleClick}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    {multiSelect && (
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => handleClick()}
                        // Stop the click from bubbling to the Card's onClick,
                        // which would toggle the fixture straight back off.
                        onClick={e => e.stopPropagation()}
                        aria-label={`Select fixture ${homeTeam} vs ${awayTeam}`}
                      />
                    )}
                    <div className="flex-1">
                      <div className="font-medium mb-1">
                        {homeTeam} vs {awayTeam}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {format(parseISO(fixture.scheduledDate), 'EEEE, MMMM dd, yyyy - h:mm a')}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Badge className={STATUS_COLORS[fixture.status] || 'bg-gray-500'}>
                      {fixture.status}
                    </Badge>
                    {fixture.status === 'finished' && fixture.homeScore !== undefined && (
                      <div className="text-sm font-semibold">
                        {fixture.homeScore} - {fixture.awayScore}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
